/**
 * Pure functions for pickleball SINGLES serve rotation.
 *
 * Kept intentionally separate from serve-logic.js (doubles) so the two
 * rule-sets never interfere with each other.
 *
 * Singles rules:
 *   - Only the server can score points.
 *   - Score call has 2 numbers: server_score – receiver_score.
 *   - Server wins rally  → server keeps serve; score increments by 1.
 *   - Server loses rally → side out; opponent becomes the new server.
 *   - No "second server" concept (each player has exactly one serve turn).
 *   - Even score (0, 2, 4 …) → serve from the RIGHT side of the court.
 *   - Odd  score (1, 3, 5 …) → serve from the LEFT  side of the court.
 *   - Game to 11, win by 2.
 */

/**
 * Returns which side of the court the server should serve from,
 * based on their current score.
 *
 * @param {number} serverScore - The serving player's current score.
 * @returns {'right' | 'left'}
 */
export function getSinglesServeSide(serverScore) {
  return serverScore % 2 === 0 ? 'right' : 'left';
}

/**
 * Computes the next serve state after a singles rally.
 *
 * @param {object} params
 * @param {string}      params.currentServerPlayerId    - Firestore player ID of the current server.
 * @param {string}      params.servingTeamId            - Firestore team ID of the serving side.
 * @param {string}      params.receivingTeamId          - Firestore team ID of the receiving side.
 * @param {boolean}     params.serverWon                - true if the server won the rally.
 * @param {string|null} params.receivingTeamPlayer1Id   - Player ID of the receiver (becomes next server on side-out).
 *
 * @returns {{
 *   nextServerTeamId:   string,
 *   nextServerPlayerId: string|null,
 *   nextServerNum:      number,   // always 1 for singles
 *   nextPos:            object,   // always {} — singles has no court-position tracking
 *   rallyType:          'point' | 'side_out',
 * }}
 */
export function computeSinglesNextServe({
  currentServerPlayerId,
  servingTeamId,
  receivingTeamId,
  serverWon,
  receivingTeamPlayer1Id,
}) {
  if (serverWon) {
    // Server wins the rally — they keep the serve, score goes up.
    return {
      nextServerTeamId:   servingTeamId,
      nextServerPlayerId: currentServerPlayerId,
      nextServerNum:      1,
      nextPos:            {},
      rallyType:          'point',
    };
  }

  // Server loses the rally — side out, opponent takes over.
  return {
    nextServerTeamId:   receivingTeamId,
    nextServerPlayerId: receivingTeamPlayer1Id,
    nextServerNum:      1,
    nextPos:            {},
    rallyType:          'side_out',
  };
}
